import {
  extractIdFromGid,
  generateGid,
  generateSrcSet,
  isPresent,
  normalizeComparableString,
} from '@theme/global-utilities';
import { readBoolean } from '@theme/utilities';
import { isCustomVariant, isCustomVariantOptionName } from '@theme/custom-variant-map';

const DEFAULT_IMAGE_SIZE = 350;
const PRODUCT_CARD_IMAGE_WIDTHS = [165, 350, 525, 700];
const PRODUCT_CARD_IMAGE_SIZES =
  '(min-width: 750px) 350px, calc((100vw - 35px) / 2)';
const FALLBACK_GALLERY_KEY = 'none';
const SWATCH_IMAGE_POSITION = 50;
/** Option names whose values are filename gallery keys (color, team, …). */
const SWATCH_OPTION_NAMES = ['color', 'colour', 'team name', 'team'];
/** Size-like options never select a filename gallery. */
const NON_GALLERY_OPTION_NAMES = ['size', 'flex', 'hand'];
const SHOPIFY_SIZE_SUFFIX =
  /[_-](?:\d+x\d*|x\d+|\d+w|pico|icon|thumb|small|compact|medium|large|grande|master)$/i;

/**
 * @typedef {object} GraphQLMoney
 * @property {string} amount
 * @property {string} currencyCode
 */

/**
 * @typedef {object} GraphQLProductImage
 * @property {string} url
 * @property {number} [width]
 * @property {number} [height]
 * @property {string|null} [altText]
 */

/**
 * @typedef {object} GraphQLOptionValue
 * @property {string} name
 */

/**
 * @typedef {object} GraphQLProductOption
 * @property {string} name
 * @property {GraphQLOptionValue[]} optionValues
 */

/**
 * @typedef {object} GraphQLSelectedOption
 * @property {string} name
 * @property {string} value
 */

/**
 * @typedef {object} GraphQLProductVariant
 * @property {string} id
 * @property {boolean} availableForSale
 * @property {number|null} quantityAvailable
 * @property {GraphQLMoney|null} price
 * @property {GraphQLMoney|null} compareAtPrice
 * @property {GraphQLProductImage|null} image
 * @property {GraphQLSelectedOption[]} selectedOptions
 * @property {Array<GraphQLMetafield|null>} metafields
 */

/**
 * @typedef {object} GraphQLMetafield
 * @property {string} key
 * @property {string} value
 */

/**
 * @typedef {{ reference: { urlParam: { value: string }, us: { value: string }, de: { value: string }, fr: { value: string }, es: { value: string }, it: { value: string }, gb: { value: string } } }} GraphQLEarlyAccessCampaign
 */

/**
 * @typedef {object} GraphQLProduct
 * @property {string} id
 * @property {string} handle
 * @property {string} title
 * @property {boolean} availableForSale
 * @property {GraphQLProductOption[]} options
 * @property {{ nodes: GraphQLProductVariant[] }} variants
 * @property {{ nodes: GraphQLProductImage[] }} images
 * @property {Array<GraphQLMetafield|null>} metafields
 * @property {GraphQLEarlyAccessCampaign|null} earlyAccessCampaign
 * @property {string[]} [tags]
 * @property {{ nodes: Array<{ id: string }> }} [collections]
 */

/**
 * @typedef {object} ProductImage
 * @property {string} src
 * @property {number} width
 * @property {number} height
 * @property {string} alt
 */

/**
 * @typedef {ProductImage & {
 *   position: number|null,
 *   variantName: string|null,
 *   variantKey: string|null,
 *   isSwatch: boolean
 * }} ProductGalleryImage
 */

/**
 * @typedef {object} ProductOptionValue
 * @property {string} name
 */

/**
 * @typedef {object} ProductOption
 * @property {string} name
 * @property {ProductOptionValue[]} values
 */

/**
 * @typedef {object} SelectedOption
 * @property {string} name
 * @property {string} value
 */

/**
 * @typedef {object} ProductVariant
 * @property {string} id
 * @property {boolean} availableForSale
 * @property {number|null} quantityAvailable
 * @property {string|null} price
 * @property {string|null} compareAtPrice
 * @property {ProductImage|null} image
 * @property {SelectedOption[]} selectedOptions
 * @property {boolean} isNewColor
 * @property {boolean} isNew
 * @property {string|null} articleCode
 * @property {string|null} variantCollectionKey
 * @property {boolean} hasCompareAtPrice
 */

/**
 * @typedef {object} ProductMediaEntry
 * @property {ProductGalleryImage[]} gallery
 * @property {ProductGalleryImage|null} swatch
 */

/**
 * @typedef {Record<string, ProductMediaEntry>} ProductMediaByVariant
 */

/**
 * @typedef {object} ProductBadge
 * @property {boolean|undefined} [isPromo]
 * @property {string|undefined} [copy]
 * @property {'product_badge_out_of_stock' | 'product_badge_sale' | 'product_badge_sold_out' | 'product_badge_coming_soon' | 'product_badge_new' | 'product_badge_new_colors' | 'product_badge_best_seller' | undefined} [copyKey]
 */

/**
 * @typedef {object} ProductFlags
 * @property {string|null} secondaryBadgeCopy
 * @property {string|null} customDeepLink
 * @property {string|null} doogmaTemplateId
 * @property {string|null} productClassification
 * @property {string|null} launchDate
 * @property {string|null} promoMessage
 * @property {boolean} isBestSeller
 * @property {string|null} outOfStockState
 * @property {string|null} captureAtOrder
 * @property {boolean} hasTennisStringing
 * @property {boolean} hasCustomBats
 * @property {string|null} earlyAccessMarketDatesJson
 * @property {string|null} earlyAccessUrlParam
 * @property {boolean} [regiosDiscountActive]
 * @property {string|null} [regiosDiscountTitle]
 */

/**
 * @typedef {object} ProductSwatch
 * @property {string} optionName
 * @property {string} value
 * @property {string} normalizedValue
 * @property {string} label
 * @property {ProductImage|ProductGalleryImage|null} image
 * @property {string} variantId
 * @property {boolean} isAvailable
 * @property {boolean} isChecked
 */

/**
 * @typedef {object} ProductCardData
 * @property {string} id
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {boolean|null} productAvailableForSale
 * @property {boolean|null} availableForSale
 * @property {number|null} quantityAvailable
 * @property {boolean|null} allVariantsOutOfStock
 * @property {string|null} price
 * @property {string|null} compareAtPrice
 * @property {ProductImage|ProductGalleryImage|undefined} image
 * @property {ProductFlags} flags
 * @property {ProductVariant|null} selectedVariant
 * @property {string|null} selectedVariantId
 * @property {ProductOption[]} options
 * @property {ProductVariant[]} variants
 * @property {ProductGalleryImage[]} galleryImages
 * @property {ProductMediaByVariant} mediaByVariant
 * @property {ProductSwatch[]} swatches
 * @property {string|null} swatchOptionName
 * @property {ProductBadge|null} productBadge
 * @property {string[]} [tags]
 * @property {number[]} [collectionIds]
 * @property {{ current: string, compare: string }|null} [regiosPrice]
 */

/**
 * @typedef {object} ProductCardSeedData
 * @property {string|number} id
 * @property {string|number} [selectedVariantId]
 * @property {string|null} [swatchOptionName]
 */

/**
 * @typedef {object} FetchProductsResult
 * @property {Map<string, GraphQLProduct>} byId
 * @property {GraphQLProduct[]} list
 */

/**
 * @typedef {'default'|'selected'|'product-line'|'compare-at'|'all'} ExplodedSwatchMode
 */

/**
 * Swatch grouping for an exploded collection card.
 * `default` is a normal card. `selected` is the existing one-swatch explode.
 * `product-line`, `compare-at`, and `all` are multi-swatch explode.
 * Product-line cards are filtered in Algolia; swatches show every color on the product.
 *
 * @typedef {object} ExplodedSwatchContext
 * @property {boolean} exploded
 * @property {boolean} multiSwatch
 * @property {string} explodeBy
 * @property {ExplodedSwatchMode} mode
 * @property {string} productLineName
 */

/**
 * @typedef {object} TransformProductOptions
 * @property {string|number|null|undefined} [selectedVariantId]
 * @property {string|null|undefined} [swatchOptionName]
 * @property {ExplodedSwatchContext} [swatchContext]
 */

/**
 * @typedef {TransformProductOptions & {
 *   enrich: boolean,
 *   fetchProductsBatched?: (ids: Array<string|number>) => Promise<FetchProductsResult>
 * }} EnrichProductOptions
 */

/**
 * @typedef {object} ProductCardImageRenderOptions
 * @property {boolean} [prioritize]
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeVariantName(value) {
  return normalizeComparableString(value);
}

/**
 * Compact a variant Article Code metafield for filename matching.
 * Filenames never include `+`; metafield values sometimes do.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function compactArticleCode(value) {
  return normalizeVariantName(String(value ?? '').replace(/\+/g, ''));
}

/**
 * @param {string|number|null|undefined} gid
 * @returns {string|null}
 */
export function extractNumericId(gid) {
  return extractIdFromGid(gid);
}

const PRODUCT_LINE_FILTER =
  /meta\.custom\.(?:product_line_collection_key|variant_collection_key)\s*:\s*(?:"([^"]*)"|'([^']*)'|([^\s)]+))/i;
const COMPARE_AT_EXPLODE_FILTER = /compare_at_price\s*>\s*0/i;

/**
 * @returns {ExplodedSwatchContext}
 */
function emptySwatchContext() {
  return {
    exploded: false,
    multiSwatch: false,
    explodeBy: '',
    mode: 'default',
    productLineName: '',
  };
}

/**
 * Product line name from an explode_by expression such as
 * meta.custom.product_line_collection_key:"Development Test".
 * The previous explode_by name, variant_collection_key, still matches.
 *
 * @param {string} explodeBy
 * @returns {string}
 */
function productLineNameFromExplodeBy(explodeBy) {
  const normalized = String(explodeBy || '')
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2018\u2019]/g, "'");
  const match = normalized.match(PRODUCT_LINE_FILTER);
  if (!match) return '';

  return (match[1] || match[2] || match[3] || '').trim();
}

/**
 * @param {boolean} exploded
 * @param {boolean} multiSwatch
 * @param {string} explodeBy
 * @returns {ExplodedSwatchMode}
 */
function resolveSwatchMode(exploded, multiSwatch, explodeBy) {
  if (!exploded) return 'default';
  if (!multiSwatch) return 'selected';

  if (productLineNameFromExplodeBy(explodeBy)) return 'product-line';
  if (COMPARE_AT_EXPLODE_FILTER.test(explodeBy)) return 'compare-at';
  if (!String(explodeBy || '').trim()) return 'all';

  return 'selected';
}

/**
 * Existing exploded-card detection.
 * Collection grids match #algolia-collection-root when the page collection
 * has custom.show_exploded_cards. Franchise carousels set
 * data-exploded-cards="true" on their own mount.
 *
 * @param {HTMLElement} scope
 * @returns {boolean}
 */
export function isExplodedCollectionCard(scope) {
  if (!scope || typeof scope.closest !== 'function') return false;

  if (scope.closest('[data-exploded-cards="true"]')) {
    return true;
  }

  const searchToolGlobal =
    /** @type {{ SearchTool?: { page?: { collection?: { showExplodedCards?: unknown } } } }} */ (
      globalThis
    );

  return Boolean(
    scope.closest('#algolia-collection-root') &&
      readBoolean(searchToolGlobal.SearchTool?.page?.collection?.showExplodedCards)
  );
}

/**
 * Exploded swatch rules for the card's mount.
 * Carousel mounts carry the collection's explode settings on the root.
 * Collection grids read window.SearchTool.page.collection.
 * Exploded vs not still comes from isExplodedCollectionCard.
 *
 * @param {HTMLElement|null|undefined} scope
 * @returns {ExplodedSwatchContext}
 */
export function readExplodedSwatchContext(scope) {
  if (!scope || typeof scope.closest !== 'function') return emptySwatchContext();

  const exploded = isExplodedCollectionCard(scope);
  const mount = scope.closest('[data-exploded-cards="true"]');

  if (mount) {
    const explodeBy = mount.getAttribute('data-request-filters') || '';
    const multiSwatch = mount.getAttribute('data-multi-swatch-explode') === 'true';

    return {
      exploded,
      multiSwatch,
      explodeBy,
      mode: resolveSwatchMode(exploded, multiSwatch, explodeBy),
      productLineName: productLineNameFromExplodeBy(explodeBy),
    };
  }

  if (!exploded) return emptySwatchContext();

  const searchToolGlobal =
    /** @type {{ SearchTool?: { page?: { collection?: { multiSwatchExplode?: unknown, explodeBy?: unknown } } } }} */ (
      globalThis
    );
  const collection = searchToolGlobal.SearchTool?.page?.collection;
  const multiSwatch = readBoolean(collection?.multiSwatchExplode);
  const explodeBy = typeof collection?.explodeBy === 'string' ? collection.explodeBy : '';

  return {
    exploded: true,
    multiSwatch,
    explodeBy,
    mode: resolveSwatchMode(true, multiSwatch, explodeBy),
    productLineName: productLineNameFromExplodeBy(explodeBy),
  };
}

/**
 * @param {ProductSwatch[]} swatches
 * @param {boolean} [showSelectedSwatchOnly]
 * @returns {{ items: ProductSwatch[], shouldRenderSwatches: boolean }}
 */
export function getFilteredSwatches(swatches, showSelectedSwatchOnly) {
  const list = Array.isArray(swatches) ? swatches : [];
  const items = showSelectedSwatchOnly
    ? list.filter((swatch) => swatch?.isChecked)
    : list;
  const shouldRenderSwatches = showSelectedSwatchOnly
    ? items.length > 0
    : items.length > 1;

  return { items, shouldRenderSwatches };
}

/**
 * @param {ProductVariant[]} variants
 * @param {ExplodedSwatchContext|null|undefined} swatchContext
 * @returns {ProductVariant[]}
 */
function variantsForSwatchContext(variants, swatchContext) {
  const mode = swatchContext?.mode || 'default';

  if (mode === 'compare-at') {
    return variants.filter((variant) => variant.hasCompareAtPrice);
  }

  return variants;
}

/**
 * @param {string|number|null|undefined} left
 * @param {string|number|null|undefined} right
 * @returns {boolean}
 */
function idsMatch(left, right) {
  if (!left || !right) return false;
  return (
    left === right ||
    (extractNumericId(left) || left) === (extractNumericId(right) || right)
  );
}

/**
 * @param {GraphQLMoney|null} value
 * @returns {string|null}
 */
function normalizeMoneyValue(value) {
  return value?.amount ?? null;
}

/**
 * @param {GraphQLMoney|null} value
 * @returns {number|null}
 */
function parseMoneyValue(value) {
  const moneyValue = normalizeMoneyValue(value);
  if (moneyValue == null || moneyValue === '') return null;

  const numeric = Number(String(moneyValue).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Matches the explode_by filter `compare_at_price > 0`, before display
 * normalization drops a compare-at that is not higher than the price.
 *
 * @param {GraphQLMoney|null} compareAtPrice
 * @returns {boolean}
 */
function hasPositiveCompareAt(compareAtPrice) {
  const compareAtValue = parseMoneyValue(compareAtPrice);

  return compareAtValue != null && compareAtValue > 0;
}

/**
 * @param {GraphQLMoney|null} compareAtPrice
 * @param {GraphQLMoney|null} price
 * @returns {string|null}
 */
function normalizeCompareAtPrice(compareAtPrice, price) {
  const compareAtValue = parseMoneyValue(compareAtPrice);
  const priceValue = parseMoneyValue(price);

  if (compareAtValue == null || compareAtValue <= 0) return null;
  if (priceValue != null && compareAtValue <= priceValue) return null;

  return normalizeMoneyValue(compareAtPrice);
}

/**
 * @param {string|number|null|undefined} value
 * @returns {string}
 */
export function formatCardPrice(value) {
  if (value == null || value === '') return '';

  const themeGlobal =
    /** @type {typeof globalThis & {
     *   formatPrice?: (currency: string|undefined, value: string|number) => string,
     *   Shopify?: { currency?: { active?: string }, locale?: string }
     * }} */ (globalThis);
  const formatter = themeGlobal.formatPrice;
  if (typeof formatter === 'function') {
    return formatter(themeGlobal.Shopify?.currency?.active, value);
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);

  try {
    return new Intl.NumberFormat(themeGlobal.Shopify?.locale || undefined, {
      style: 'currency',
      currency: themeGlobal.Shopify?.currency?.active || 'USD',
    }).format(numeric);
  } catch {
    return `$${numeric}`;
  }
}

/**
 * @param {string|number|null|undefined} value
 * @returns {number|null}
 */
export function parseCardPriceValue(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const numeric = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * @param {number|null|undefined} value
 * @returns {number|null}
 */
function normalizeInventory(value) {
  const quantity = Number(value);
  return Number.isFinite(quantity) ? quantity : null;
}

/**
 * @param {GraphQLProductImage|null} image
 * @returns {ProductImage|null}
 */
export function normalizeProductImage(image) {
  if (!image) return null;

  if (!image.url) return null;

  return {
    src: image.url,
    width: image.width || DEFAULT_IMAGE_SIZE,
    height: image.height || DEFAULT_IMAGE_SIZE,
    alt: image.altText || '',
  };
}

/**
 * @param {string|null|undefined} src
 * @param {number} width
 * @param {number} height
 * @returns {string}
 */
export function imageUrlWithSize(src, width, height) {
  if (!src) return '';

  const url = new URL(src, window.location.origin);
  url.searchParams.set('width', String(width));
  url.searchParams.set('height', String(height));
  return url.toString();
}

/**
 * @param {HTMLImageElement} imgEl
 * @param {{ src: string, width?: number, height?: number, alt?: string }} imgData
 * @param {string} fallbackAlt
 * @param {ProductCardImageRenderOptions} [options]
 */
/**
 * Filename of a Shopify CDN image, ignoring width and host.
 * @param {string} src
 * @returns {string}
 */
function imageFileKey(src) {
  try {
    const url = new URL(src, window.location.origin);
    return decodeURIComponent(url.pathname.split('/').pop() || '').toLowerCase();
  } catch {
    return '';
  }
}

/**
 * Fades a card image in when its first real image finishes loading, instead of
 * snapping from the empty image well. Later swaps (variant changes) keep the
 * old image on screen until the new one is ready, so they never blink.
 * The LCP card is skipped (desktop shows its base image, which is applied
 * without `prioritize`), because opacity 0 would delay LCP.
 *
 * @param {HTMLImageElement} imgEl
 */
function fadeInFirstProductCardImage(imgEl) {
  if (imgEl.dataset.cardImageLoaded === 'true') return;
  if (imgEl.closest('[data-priority-image]')) return;

  imgEl.classList.add('is-loading');
  const done = () => {
    imgEl.dataset.cardImageLoaded = 'true';
    imgEl.classList.remove('is-loading');
  };
  imgEl.addEventListener('load', done, { once: true });
  imgEl.addEventListener('error', done, { once: true });
}

export function applyProductCardImage(
  imgEl,
  imgData,
  fallbackAlt,
  options = {}
) {
  // Priority has to be set before src. Assigning src starts the request,
  // and a later fetchpriority does not raise it.
  if (options.prioritize) {
    imgEl.loading = 'eager';
    imgEl.setAttribute('fetchpriority', 'high');
    imgEl.classList.remove('is-loading');
    imgEl.dataset.cardImageLoaded = 'true';
  } else if (!imgEl.getAttribute('loading')) {
    imgEl.loading = 'lazy';
  }

  imgEl.decoding = imgEl.decoding || 'async';

  const {
    src,
    srcset,
    sizes: sizeAttr,
  } = generateSrcSet(
    imgData.src,
    PRODUCT_CARD_IMAGE_WIDTHS,
    String(DEFAULT_IMAGE_SIZE),
    PRODUCT_CARD_IMAGE_SIZES
  );

  const currentSrc = imgEl.getAttribute('src') || '';
  const sameFile =
    currentSrc &&
    imgEl.getAttribute('srcset') &&
    imageFileKey(currentSrc) === imageFileKey(src);

  imgEl.alt = imgData.alt || fallbackAlt || imgEl.alt || '';

  if (sameFile) {
    if (sizeAttr && !imgEl.getAttribute('sizes')) imgEl.sizes = sizeAttr;
    return;
  }

  if (!options.prioritize) fadeInFirstProductCardImage(imgEl);

  imgEl.src = src;
  imgEl.srcset = srcset;
  imgEl.sizes = sizeAttr || '';
  if (typeof imgData.width === 'number') imgEl.width = imgData.width;
  if (typeof imgData.height === 'number') imgEl.height = imgData.height;
}

/**
 * @param {ProductImage} image
 * @returns {string}
 */
function getImageBasename(image) {
  const url = new URL(image.src, window.location.origin);
  return decodeURIComponent(url.pathname.split('/').pop() || '').replace(
    /\.[^.]+$/,
    ''
  );
}

/**
 * @param {ProductImage} image
 * @returns {ProductGalleryImage}
 */
function parseGalleryImage(image) {
  const cleanName = getImageBasename(image).replace(SHOPIFY_SIZE_SUFFIX, '');
  const match = cleanName.match(/[_-]([^_]+)[_-](\d+)$/);
  const position = match ? Number(match[2]) : null;
  const variantName = match?.[1] || null;

  return {
    ...image,
    position: Number.isFinite(position) ? position : null,
    variantName,
    variantKey: variantName ? normalizeVariantName(variantName) : null,
    isSwatch: position === SWATCH_IMAGE_POSITION,
  };
}

/**
 * @param {ProductOption[]} options
 * @param {string|null|undefined} optionName
 * @returns {ProductOptionValue[]}
 */
function getOptionValues(options, optionName) {
  const optionKey = normalizeVariantName(optionName);
  return (
    options.find((option) => normalizeVariantName(option.name) === optionKey)
      ?.values || []
  );
}

/**
 * @param {ProductOption[]} options
 * @param {string|null|undefined} optionName
 * @returns {Map<string, number>}
 */
function buildOptionOrder(options, optionName) {
  return new Map(
    getOptionValues(options, optionName).map((value, index) => [
      normalizeVariantName(value.name),
      index,
    ])
  );
}

/**
 * @param {ProductGalleryImage} image
 * @returns {number}
 */
function imagePosition(image) {
  return typeof image.position === 'number' ? image.position : 9999;
}

/**
 * @param {ProductGalleryImage[]} images
 * @param {Map<string, number>} [optionOrder]
 * @returns {ProductGalleryImage[]}
 */
function sortGalleryImages(images, optionOrder = new Map()) {
  const firstSeen = new Map();

  images.forEach((image) => {
    const key = image.variantKey || FALLBACK_GALLERY_KEY;
    if (!firstSeen.has(key)) firstSeen.set(key, firstSeen.size);
  });

  return [...images].sort((a, b) => {
    const aKey = a.variantKey || FALLBACK_GALLERY_KEY;
    const bKey = b.variantKey || FALLBACK_GALLERY_KEY;
    const aOrder = optionOrder.has(aKey)
      ? optionOrder.get(aKey)
      : firstSeen.get(aKey);
    const bOrder = optionOrder.has(bKey)
      ? optionOrder.get(bKey)
      : firstSeen.get(bKey);

    return (
      (aOrder ?? 9999) - (bOrder ?? 9999) ||
      imagePosition(a) - imagePosition(b)
    );
  });
}

/**
 * @param {ProductGalleryImage[]} images
 * @returns {ProductMediaByVariant}
 */
function buildMediaByVariant(images) {
  return images.reduce((media, image) => {
    const key = image.variantKey || FALLBACK_GALLERY_KEY;
    const entry = media[key] || { gallery: [], swatch: null };

    if (image.isSwatch) {
      entry.swatch = image;
    } else {
      entry.gallery.push(image);
    }

    media[key] = entry;
    return media;
  }, /** @type {ProductMediaByVariant} */ ({}));
}

/**
 * @param {GraphQLProductOption} option
 * @returns {ProductOption|null}
 */
function normalizeProductOption(option) {
  const values = option.optionValues
    .map((value) => {
      return value.name ? { name: value.name } : null;
    })
    .filter(isPresent);

  return option.name ? { name: option.name, values } : null;
}

/**
 * @param {GraphQLProductVariant} variant
 * @returns {SelectedOption[]}
 */
function normalizeSelectedOptions(variant) {
  return variant.selectedOptions
    .map((option) =>
      option.name && option.value
        ? { name: option.name, value: option.value }
        : null
    )
    .filter(isPresent);
}

/**
 * @param {GraphQLProductVariant} variant
 * @returns {ProductVariant|null}
 */
function normalizeProductVariant(variant) {
  if (!variant.id) return null;

  return {
    id: variant.id,
    availableForSale: Boolean(variant.availableForSale),
    quantityAvailable: normalizeInventory(variant.quantityAvailable),
    price: normalizeMoneyValue(variant.price),
    compareAtPrice: normalizeCompareAtPrice(
      variant.compareAtPrice,
      variant.price
    ),
    image: normalizeProductImage(variant.image),
    selectedOptions: normalizeSelectedOptions(variant),
    isNewColor: getMetafieldValue(variant.metafields, 'new_color') === 'true',
    isNew: readBoolean(getMetafieldValue(variant.metafields, 'variant_is_new')),
    articleCode: getMetafieldValue(variant.metafields, 'article_code'),
    variantCollectionKey:
      getMetafieldValue(variant.metafields, 'product_line_collection_key') ||
      getMetafieldValue(variant.metafields, 'variant_collection_key'),
    hasCompareAtPrice: hasPositiveCompareAt(variant.compareAtPrice),
  };
}

/**
 * @param {ProductVariant|null|undefined} variant
 * @param {string|null|undefined} optionName
 * @returns {string|null}
 */
export function getVariantOptionValue(variant, optionName) {
  const optionKey = normalizeVariantName(optionName);
  return (
    variant?.selectedOptions?.find(
      (option) => normalizeVariantName(option.name) === optionKey
    )?.value || null
  );
}

/**
 * @param {ProductVariant|null|undefined} variant
 * @returns {boolean}
 */
export function isVariantAvailable(variant) {
  if (!variant?.availableForSale) return false;
  return typeof variant.quantityAvailable === 'number'
    ? variant.quantityAvailable > 0
    : true;
}

/**
 * @param {ProductVariant[]} variants
 * @returns {ProductVariant|null}
 */
export function chooseFirstAvailableVariant(variants) {
  return variants.find(isVariantAvailable) || variants[0] || null;
}

/**
 * @param {ProductCardData} product
 * @param {string|null|undefined} optionValue
 * @returns {ProductGalleryImage[]}
 */
export function getGalleryForOptionValue(product, optionValue) {
  const mediaByVariant = product.mediaByVariant || {};
  const key = normalizeVariantName(optionValue);
  const exactGallery = key ? mediaByVariant[key]?.gallery : null;
  const fallbackGallery = mediaByVariant[FALLBACK_GALLERY_KEY]?.gallery;

  if (exactGallery?.length) return exactGallery;
  if (fallbackGallery?.length) return fallbackGallery;

  return (product.galleryImages || []).filter(
    (image) =>
      !image.isSwatch &&
      (!key || image.variantKey === key || !image.variantKey)
  );
}

/**
 * @param {string|null|undefined} optionName
 * @returns {boolean}
 */
function isNonGalleryOptionName(optionName) {
  const key = normalizeVariantName(optionName);
  return NON_GALLERY_OPTION_NAMES.some(
    (name) => key === normalizeVariantName(name)
  );
}

/**
 * @param {string|null|undefined} optionName
 * @returns {boolean}
 */
function isSwatchOptionName(optionName) {
  const key = normalizeVariantName(optionName);
  if (!key || isNonGalleryOptionName(optionName)) return false;

  return SWATCH_OPTION_NAMES.some((name) => {
    const swatchKey = normalizeVariantName(name);
    return key === swatchKey || key.includes(swatchKey);
  });
}

/**
 * Use the requested swatch option when the product actually has it.
 * Otherwise pick Color / Team from the product's own option names.
 * NFL exploded cards pass `color` while the option is `team`.
 *
 * @param {ProductOption[]} options
 * @param {string|null|undefined} requestedName
 * @returns {string|null}
 */
function resolveCardSwatchOptionName(options, requestedName) {
  const names = (options || []).map((option) => option?.name).filter(isPresent);
  const requestedKey = normalizeVariantName(requestedName);

  if (requestedKey) {
    const requested = names.find(
      (name) => normalizeVariantName(name) === requestedKey
    );
    if (requested) return requested;
  }

  return names.find((name) => isSwatchOptionName(name)) || requestedName || null;
}

/**
 * @param {ProductCardData} product
 * @param {string|null|undefined} optionValue
 * @returns {boolean}
 */
function hasGalleryForOptionValue(product, optionValue) {
  const key = normalizeVariantName(optionValue);
  return Boolean(key && product.mediaByVariant?.[key]?.gallery?.length);
}

/**
 * @param {ProductCardData} product
 * @param {ProductVariant|null|undefined} variant
 * @returns {string|null}
 */
function findOptionValueWithGallery(product, variant) {
  const match = (variant?.selectedOptions || []).find((option) => {
    if (!option?.value || isNonGalleryOptionName(option.name)) return false;
    return hasGalleryForOptionValue(product, option.value);
  });

  return match?.value || null;
}

/**
 * Color/team option value when the card has swatches; otherwise the variant
 * Article Code when that token has its own gallery. Leaves `_none_` / mixed
 * galleries to `getGalleryForOptionValue` so color/team cards stay unchanged.
 *
 * When the requested swatch name is not on the product (exploded NFL cards
 * look up `color` while the option is `team`), use the selected option whose
 * value matches a filename token. That keeps the exploded variant's images
 * instead of the default team's gallery.
 *
 * @param {ProductCardData} product
 * @param {ProductVariant|null|undefined} variant
 * @param {string|null|undefined} swatchOptionName
 * @returns {string|null}
 */
function getCardGalleryKey(product, variant, swatchOptionName) {
  const optionValue = getVariantOptionValue(variant, swatchOptionName);
  if (optionValue) return optionValue;

  const galleryOptionValue = findOptionValueWithGallery(product, variant);
  if (galleryOptionValue) return galleryOptionValue;

  const articleKey = compactArticleCode(variant?.articleCode);
  if (articleKey && product.mediaByVariant?.[articleKey]?.gallery?.length) {
    return articleKey;
  }

  return null;
}

/**
 * @param {ProductCardData} product
 * @param {string|null|undefined} swatchOptionName
 * @returns {ProductImage[]}
 */
export function getProductCardImages(product, swatchOptionName) {
  const galleryKey = getCardGalleryKey(
    product,
    product.selectedVariant,
    swatchOptionName
  );
  const gallery = getGalleryForOptionValue(product, galleryKey);
  const sourceImages = gallery.length
    ? gallery
    : [product.image, product.selectedVariant?.image, ...(product.galleryImages || [])].filter(
        isPresent
      );
  const seen = new Set();

  return sourceImages.filter((image) => {
    const isSwatch = 'isSwatch' in image && image.isSwatch;
    if (isSwatch || seen.has(image.src)) return false;
    seen.add(image.src);
    return true;
  });
}

/**
 * @param {ProductCardData} product
 * @param {string|null|undefined} optionValue
 * @returns {ProductGalleryImage|null}
 */
function getSwatchImageForOptionValue(product, optionValue) {
  const key = normalizeVariantName(optionValue);
  return key ? product.mediaByVariant?.[key]?.swatch || null : null;
}

/**
 * @param {string} url
 * @param {string|null|undefined} variantId
 * @returns {string}
 */
function withVariantParam(url, variantId) {
  if (!url) return '';

  const numericVariantId = extractNumericId(variantId) || variantId;
  if (!numericVariantId) return url;

  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.set('variant', numericVariantId);
  return parsed.pathname + parsed.search + parsed.hash;
}

/**
 * @param {ProductCardData} product
 * @param {string|null|undefined} swatchOptionName
 * @param {ProductVariant|null} selectedVariant
 * @param {ExplodedSwatchContext|null|undefined} [swatchContext]
 * @returns {ProductSwatch[]}
 */
export function buildSwatches(product, swatchOptionName, selectedVariant, swatchContext) {
  const sourceVariants = variantsForSwatchContext(product.variants, swatchContext);
  if (!swatchOptionName || !sourceVariants.length) return [];

  const optionValues = getOptionValues(product.options, swatchOptionName);
  const values = optionValues.map((value) => value.name);
  const selectedKey = normalizeVariantName(
    getVariantOptionValue(selectedVariant, swatchOptionName)
  );

  return values
    .map((value) => {
      const valueKey = normalizeVariantName(value);
      const variants = sourceVariants.filter(
        (variant) =>
          normalizeVariantName(
            getVariantOptionValue(variant, swatchOptionName)
          ) === valueKey
      );
      const representativeVariant = chooseFirstAvailableVariant(variants);
      if (!representativeVariant) return null;

      return {
        optionName: swatchOptionName,
        value,
        normalizedValue: valueKey,
        label: value,
        image:
          getSwatchImageForOptionValue(product, value) ||
          representativeVariant.image ||
          getGalleryForOptionValue(product, value)[0] ||
          product.image ||
          null,
        variantId: representativeVariant.id,
        isAvailable: variants.some(isVariantAvailable),
        isChecked: valueKey === selectedKey,
      };
    })
    .filter(isPresent);
}

/**
 * @param {ProductCardData} product
 * @param {ProductVariant|null} selectedVariant
 * @param {boolean} [hasSwatches]
 * @returns {ProductBadge}
 */
function buildProductBadgeValue(product, selectedVariant, hasSwatches) {

  const selectedSwatch = product.swatches?.find((swatch) => swatch.isChecked);
  const currentColorOutOfStock = selectedSwatch
    ? !selectedSwatch.isAvailable
    : !product.variants.some(isVariantAvailable);
  if (currentColorOutOfStock) {
    return { copyKey: 'product_badge_sold_out' };
  }

  const launchDate = product.flags.launchDate;
  const futureLaunch = !!launchDate ? new Date(launchDate) > new Date() : false;
  if (futureLaunch) {
    return { copyKey: 'product_badge_coming_soon' };
  }

  const promoMsg = String(product.flags.promoMessage || '').trim();
  if (promoMsg) {
    return { copy: promoMsg, isPromo: true };
  }

  // Regios discount title occupies the promo slot when promo_message is empty
  if (product.flags.regiosDiscountActive && product.flags.regiosDiscountTitle) {
    return { copy: product.flags.regiosDiscountTitle, isPromo: true };
  }

  if (selectedVariant?.compareAtPrice) {
    return { copyKey: 'product_badge_sale', isPromo: true };
  }

  if (selectedVariant?.isNew) {
    return { copyKey: 'product_badge_new' };
  }

  const anyNewColors = product?.variants?.some(
    (variant) => !!variant && variant.isNewColor,
  );
  if (anyNewColors) {
    return { copyKey: 'product_badge_new_colors' };
  }

  if (product.flags.isBestSeller) {
    return { copyKey: 'product_badge_best_seller' };
  }

  return {};
}

/**
 * @param {ProductCardData} product
 * @param {ProductVariant|null} selectedVariant
 * @param {string|null|undefined} swatchOptionName
 * @param {ExplodedSwatchContext|null|undefined} [swatchContext]
 * @returns {ProductCardData}
 */
export function buildProductCardStateForVariant(
  product,
  selectedVariant,
  swatchOptionName,
  swatchContext
) {
  const variant = selectedVariant || product.selectedVariant || null;
  const galleryKey = getCardGalleryKey(product, variant, swatchOptionName);
  const image =
    getGalleryForOptionValue(product, galleryKey)[0] ||
    variant?.image ||
    product.image ||
    null;
  const nextProduct = {
    ...product,
    selectedVariant: variant,
    selectedVariantId: variant?.id || product.selectedVariantId || null,
    productAvailableForSale:
      product.productAvailableForSale ?? product.availableForSale ?? null,
    availableForSale: variant?.availableForSale ?? product.availableForSale,
    price: variant?.price ?? product.price ?? null,
    compareAtPrice: variant
      ? variant.compareAtPrice ?? null
      : product.compareAtPrice ?? null,
    image: image || undefined,
    url: withVariantParam(product.url, variant?.id),
    regiosPrice: null,
    flags: { 
      ...(product.flags || {}),
      regiosDiscountActive: false,
      regiosDiscountTitle: null,
    },
  };

  const swatches = buildSwatches(nextProduct, swatchOptionName, variant, swatchContext);
  const productBadge = computeCardBadge({ ...nextProduct, swatches });

  return {
    ...nextProduct,
    swatches,
    productBadge,
  };
}

/**
 * Resolves the image-badge value for a card
 * @param {ProductCardData} product - card data with selectedVariant + swatches
 * @returns {ProductBadge}
 */
export function computeCardBadge(product) {
  const elem = /** @type {HTMLElement|null} */ (
    document.querySelector('.algolia-search-tool')
  );
  const isExploded = elem ? isExplodedCollectionCard(elem) : false;
  const { shouldRenderSwatches } = getFilteredSwatches(
    product.swatches || [],
    isExploded
  );
  return buildProductBadgeValue(
    product,
    product.selectedVariant || null,
    shouldRenderSwatches
  );
}

/**
 * @param {Array<GraphQLMetafield|null>|null|undefined} metafields
 * @param {string} key
 * @returns {string|null}
 */
function getMetafieldValue(metafields, key) {
  return (metafields || []).find((metafield) => metafield?.key === key)?.value ?? null;
}

/**
 * @param {FetchProductsResult} result
 * @param {string|number} productId
 * @returns {GraphQLProduct|null}
 */
function findFetchedProduct(result, productId) {
  const productGid = generateGid(productId) || String(productId);

  return (
    result.byId.get(productGid) ||
    result.byId.get(String(productId)) ||
    result.list?.find((product) => idsMatch(product?.id, productGid)) ||
    null
  );
}

/**
 * @param {GraphQLProduct|null} product
 * @param {TransformProductOptions} [options]
 * @returns {ProductCardData|null}
 */
export function transformProductMetadataToCardData(product, options = {}) {
  if (!product) return null;

  const raw = product;
  const metafields = raw.metafields;
  // Cards are stock-only: drop the hidden Custom Strung / Customization option and its custom
  // variants, so availability, price and the selected variant follow stock (WIL26D2C-623).
  const productOptions = raw.options
    .map(normalizeProductOption)
    .filter(isPresent)
    .filter((option) => !isCustomVariantOptionName(option.name));
  const swatchOptionName = resolveCardSwatchOptionName(
    productOptions,
    options.swatchOptionName || null
  );
  const optionOrder = buildOptionOrder(productOptions, swatchOptionName);
  const galleryImages = sortGalleryImages(
    raw.images.nodes
      .map(normalizeProductImage)
      .filter(isPresent)
      .map(parseGalleryImage),
    optionOrder
  );
  const mediaByVariant = buildMediaByVariant(galleryImages);
  const variants = raw.variants.nodes
    .map(normalizeProductVariant)
    .filter(isPresent)
    .filter((variant) => !isCustomVariant(variant.selectedOptions));
  const allVariantsOutOfStock = variants.length
    ? !variants.some(isVariantAvailable)
    : !productAvailableForSale;
  const selectedVariant =
    variants.find((variant) => idsMatch(variant.id, options.selectedVariantId)) ||
    chooseFirstAvailableVariant(variants);
  const productAvailableForSale = Boolean(raw.availableForSale);
  const rootStr = Theme?.routes?.root || '/'
  const root = rootStr?.endsWith('/') ? rootStr : `${rootStr}/`;
  const baseUrl = raw.handle ? `${root}products/${raw.handle}` : '';
  const formatted = {
    id: raw.id,
    handle: raw.handle || '',
    title: raw.title || '',
    url: baseUrl,
    productAvailableForSale,
    availableForSale: selectedVariant?.availableForSale ?? null,
    quantityAvailable: selectedVariant?.quantityAvailable ?? null,
    price: selectedVariant?.price ?? null,
    compareAtPrice: selectedVariant?.compareAtPrice ?? null,
    image: galleryImages[0] || undefined,
    allVariantsOutOfStock,
    productBadge: null,
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    collectionIds: (raw.collections?.nodes || [])
      .map((collection) => extractIdFromGid(collection?.id))
      .filter(isPresent)
      .map(Number),
    flags: {
      secondaryBadgeCopy: getMetafieldValue(metafields, 'secondary_badge_copy'),
      customDeepLink: getMetafieldValue(metafields, 'custom_deep_link'),
      doogmaTemplateId: getMetafieldValue(metafields, 'doogma_template_id'),
      productClassification: getMetafieldValue(
        metafields,
        'product_classification'
      ),
      launchDate: getMetafieldValue(product.metafields, 'launch_date'),
      promoMessage: getMetafieldValue(product.metafields, 'promo_message'),
      isBestSeller: getMetafieldValue(product.metafields, 'is_best_seller') === 'true',
      outOfStockState: getMetafieldValue(metafields, 'out_of_stock_state'),
      captureAtOrder: getMetafieldValue(metafields, 'capture_at_order'),
      hasTennisStringing: Boolean(getMetafieldValue(metafields, 'tennis_stringing_options')),
      hasCustomBats: Boolean(getMetafieldValue(metafields, 'custom_bats')),
      earlyAccessMarketDatesJson: product?.earlyAccessCampaign?.reference
        ? JSON.stringify({
            us: product.earlyAccessCampaign.reference.us?.value ?? '',
            de: product.earlyAccessCampaign.reference.de?.value ?? '',
            fr: product.earlyAccessCampaign.reference.fr?.value ?? '',
            es: product.earlyAccessCampaign.reference.es?.value ?? '',
            it: product.earlyAccessCampaign.reference.it?.value ?? '',
            gb: product.earlyAccessCampaign.reference.gb?.value ?? '',
          })
        : null,
      earlyAccessUrlParam:
        product?.earlyAccessCampaign?.reference?.urlParam?.value ?? null,
      regiosDiscountActive: false,
      regiosDiscountTitle: null,
    },
    selectedVariant,
    selectedVariantId: selectedVariant?.id || null,
    options: productOptions,
    variants,
    galleryImages,
    mediaByVariant,
    swatches: [],
    swatchOptionName,
  };

  return buildProductCardStateForVariant(
    formatted,
    selectedVariant,
    swatchOptionName,
    options.swatchContext
  );
}

/** @type {Map<string, Promise<GraphQLProduct|null>>} */
const ajaxProductCache = new Map();

/**
 * Storefront `nodes` returns null for products that are on the Online Store
 * but not on the theme token's channel. `/products/{handle}.js` still has
 * the variant list. Product-line keys are overlaid from Algolia.
 *
 * @param {string|number} productId
 * @returns {Promise<GraphQLProduct|null>}
 */
function loadAjaxProduct(productId) {
  const cacheKey = String(productId);
  const cached = ajaxProductCache.get(cacheKey);
  if (cached) return cached;

  const pending = fetchAjaxProductGraph(productId);
  ajaxProductCache.set(cacheKey, pending);
  return pending;
}

/**
 * @param {string|number} productId
 * @returns {Promise<GraphQLProduct|null>}
 */
async function fetchAjaxProductGraph(productId) {
  const hits = await fetchAlgoliaVariantHits(productId);
  const handle = hits.find((hit) => hit?.handle)?.handle;
  if (!handle) return null;

  const ajax = await fetchStorefrontProductJson(handle);
  if (!ajax) return null;

  return ajaxProductToGraphql(ajax, productLineKeysByVariantId(hits));
}

/**
 * @param {string|number} productId
 * @returns {Promise<object[]>}
 */
async function fetchAlgoliaVariantHits(productId) {
  const searchTool =
    /** @type {{ SearchTool?: { algolia?: { appId?: string, apiKey?: string, productIndexName?: string } } }} */ (
      globalThis
    ).SearchTool;
  const config = searchTool?.algolia;
  if (!config?.appId || !config.apiKey || !config.productIndexName) return [];

  const response = await fetch(
    `https://${config.appId}-dsn.algolia.net/1/indexes/${encodeURIComponent(config.productIndexName)}/query`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Algolia-Application-Id': config.appId,
        'X-Algolia-API-Key': config.apiKey,
      },
      body: JSON.stringify({
        query: '',
        filters: `id=${productId}`,
        distinct: 0,
        hitsPerPage: 100,
        attributesToRetrieve: ['objectID', 'handle', 'meta'],
      }),
    }
  );
  if (!response.ok) return [];

  const payload = await response.json();
  return Array.isArray(payload.hits) ? payload.hits : [];
}

/**
 * @param {object[]} hits
 * @returns {Map<string, string>}
 */
function productLineKeysByVariantId(hits) {
  const keys = new Map();

  for (const hit of hits) {
    const variantId = hit?.objectID;
    const value = hit?.meta?.custom?.product_line_collection_key;
    if (!variantId || value == null || value === '') continue;

    keys.set(
      String(variantId),
      Array.isArray(value) ? JSON.stringify(value) : String(value)
    );
  }

  return keys;
}

/**
 * @param {string} handle
 * @returns {Promise<object|null>}
 */
async function fetchStorefrontProductJson(handle) {
  const themeGlobal =
    /** @type {{ Theme?: { routes?: { root?: string } }, Shopify?: { routes?: { root?: string } } }} */ (
      globalThis
    );
  const root = themeGlobal.Theme?.routes?.root || themeGlobal.Shopify?.routes?.root || '/';
  const prefix = root.endsWith('/') ? root : `${root}/`;
  const response = await fetch(`${prefix}products/${encodeURIComponent(handle)}.js`);
  if (!response.ok) return null;

  return response.json();
}

/**
 * @param {number|string|null|undefined} cents
 * @param {string} currency
 * @returns {GraphQLMoney|null}
 */
function centsToMoney(cents, currency) {
  if (cents == null || cents === '') return null;

  const numeric = Number(cents);
  if (!Number.isFinite(numeric)) return null;

  return { amount: (numeric / 100).toFixed(2), currencyCode: currency };
}

/**
 * @param {string|object|null|undefined} image
 * @returns {GraphQLProductImage|null}
 */
function ajaxImageToGraphql(image) {
  if (!image) return null;

  if (typeof image === 'string') {
    return image ? { url: image, altText: '', width: null, height: null } : null;
  }

  const url = image.src || image.url;
  if (!url) return null;

  return {
    url,
    altText: image.alt || image.altText || '',
    width: image.width || null,
    height: image.height || null,
  };
}

/**
 * @param {object} ajax
 * @param {Map<string, string>} lineKeys
 * @returns {GraphQLProduct}
 */
function ajaxProductToGraphql(ajax, lineKeys) {
  const currency =
    /** @type {{ Shopify?: { currency?: { active?: string } } }} */ (globalThis)
      .Shopify?.currency?.active || 'USD';
  const optionNames = (ajax.options || []).map((option) => option.name).filter(Boolean);

  return {
    id: `gid://shopify/Product/${ajax.id}`,
    handle: ajax.handle || '',
    title: ajax.title || '',
    availableForSale: Boolean(ajax.available),
    tags: Array.isArray(ajax.tags) ? ajax.tags : [],
    collections: { nodes: [] },
    options: (ajax.options || []).map((option) => ({
      name: option.name,
      optionValues: (option.values || []).map((name) => ({ name })),
    })),
    images: {
      nodes: (ajax.images || []).map(ajaxImageToGraphql).filter(isPresent),
    },
    variants: {
      nodes: (ajax.variants || []).map((variant) => {
        const lineKey = lineKeys.get(String(variant.id)) || null;

        return {
          id: `gid://shopify/ProductVariant/${variant.id}`,
          availableForSale: Boolean(variant.available),
          quantityAvailable: null,
          price: centsToMoney(variant.price, currency),
          compareAtPrice: centsToMoney(variant.compare_at_price, currency),
          image: ajaxImageToGraphql(variant.featured_image),
          selectedOptions: optionNames
            .map((name, index) => {
              const value = variant[`option${index + 1}`];
              return value ? { name, value } : null;
            })
            .filter(isPresent),
          metafields: [
            { key: 'new_color', value: null },
            { key: 'variant_is_new', value: null },
            { key: 'article_code', value: null },
            { key: 'product_line_collection_key', value: lineKey },
          ],
        };
      }),
    },
    metafields: [],
    earlyAccessCampaign: null,
  };
}

/**
 * @param {ProductCardSeedData|GraphQLProduct|ProductCardData} product
 * @param {EnrichProductOptions} options
 * @returns {Promise<ProductCardSeedData|GraphQLProduct|ProductCardData>}
 */
export async function enrichIfMinimal(product, options) {
  const transformOptions = {
    selectedVariantId: options.selectedVariantId,
    swatchOptionName: options.swatchOptionName,
    swatchContext: options.swatchContext,
  };

  if (!options.enrich) {
    return product;
  }

  const productId = product?.id;
  if (!productId || !options.fetchProductsBatched) return product;

  try {
    const result = await options.fetchProductsBatched([productId]);
    const fetchedProduct = findFetchedProduct(result, productId);
    const card = transformProductMetadataToCardData(fetchedProduct, transformOptions);
    if (card?.title) return card;
  } catch (error) {
    console.warn('[product-card-custom] Failed to fetch product metadata', error);
  }

  try {
    const ajaxProduct = await loadAjaxProduct(productId);
    const card = transformProductMetadataToCardData(ajaxProduct, transformOptions);
    if (card?.title) return card;
  } catch (error) {
    console.warn('[product-card-custom] Failed to fetch product JSON', error);
  }

  return product;
}
